import React from 'react';
import { Checkbox, Form, Input, InputNumber, Select, Space } from 'antd';
import type { ExpV2Targeting, ExpV2UserList } from '@/types/experimentsV2';

interface Props {
  value: ExpV2Targeting;
  lists: ExpV2UserList[];
  onChange: (value: ExpV2Targeting) => void;
}

const upperCodes = (codes: string[]) => codes.map((c) => c.trim().toUpperCase()).filter(Boolean);

const RangeInput: React.FC<{
  id: string;
  value?: { min?: number; max?: number };
  onChange: (v: { min?: number; max?: number }) => void;
}> = ({ id, value, onChange }) => (
  <Space>
    <InputNumber
      id={`${id}-min`}
      aria-label="From"
      placeholder="from"
      min={0}
      precision={0}
      value={value?.min}
      onChange={(n) => onChange({ ...value, min: n ?? undefined })}
    />
    <InputNumber
      id={`${id}-max`}
      aria-label="To"
      placeholder="to"
      min={0}
      precision={0}
      value={value?.max}
      onChange={(n) => onChange({ ...value, max: n ?? undefined })}
    />
  </Space>
);

/** Empty fields mean "everyone"; they are dropped before saving. The server checks the rest. */
export const TargetingEditor: React.FC<Props> = ({ value, lists, onChange }) => {
  const set = (patch: Partial<ExpV2Targeting>) => onChange({ ...value, ...patch });
  return (
    <div data-section="targeting">
      <Form.Item label="Platforms" extra="None ticked means every platform.">
        <Checkbox.Group
          value={value.platforms ?? []}
          options={[
            { value: 'android', label: 'Android' },
            { value: 'ios', label: 'iOS' },
          ]}
          onChange={(v) => set({ platforms: v as string[] })}
        />
      </Form.Item>
      <Form.Item label="Countries (include)" extra="ISO-2 codes the game sends, e.g. IN, PK.">
        <Select
          id="expv2-countries-include"
          mode="tags"
          value={value.countries?.include ?? []}
          tokenSeparators={[',', ' ']}
          onChange={(v: string[]) =>
            set({ countries: { ...value.countries, include: upperCodes(v) } })
          }
        />
      </Form.Item>
      <Form.Item label="Countries (exclude)">
        <Select
          id="expv2-countries-exclude"
          mode="tags"
          value={value.countries?.exclude ?? []}
          tokenSeparators={[',', ' ']}
          onChange={(v: string[]) =>
            set({ countries: { ...value.countries, exclude: upperCodes(v) } })
          }
        />
      </Form.Item>
      <Form.Item label="Payer">
        <Select
          id="expv2-payer"
          value={value.payer ?? 'any'}
          options={[
            { value: 'any', label: 'Everyone' },
            { value: 'free', label: 'Never paid' },
            { value: 'payer', label: 'Paid at least once' },
          ]}
          onChange={(v: string) =>
            set({ payer: v === 'any' ? undefined : (v as ExpV2Targeting['payer']) })
          }
        />
      </Form.Item>
      <Form.Item label="Days since install (IST)">
        <RangeInput
          id="expv2-days"
          value={value.daysSinceInstall}
          onChange={(daysSinceInstall) => set({ daysSinceInstall })}
        />
      </Form.Item>
      <Form.Item label="Player level">
        <RangeInput id="expv2-level" value={value.level} onChange={(level) => set({ level })} />
      </Form.Item>
      <Form.Item label="Minimum app version" extra="Players on older versions are not targeted.">
        <Input
          id="expv2-target-min-version"
          placeholder="e.g. 0.4.5"
          value={value.minAppVersion ?? ''}
          onChange={(e) => set({ minAppVersion: e.target.value })}
        />
      </Form.Item>
      <Form.Item label="User list" extra="Only players on this list are targeted.">
        <Select
          id="expv2-user-list"
          allowClear
          placeholder="No list"
          value={value.userListId}
          options={lists.map((l) => ({ value: l.id, label: `${l.name} (${l.size})` }))}
          onChange={(v?: string) => set({ userListId: v })}
        />
      </Form.Item>
    </div>
  );
};
